/**
 * Measure the element and draw 1:1 instead of stretching a fixed viewBox.
 *
 * A fixed `viewBox` with `preserveAspectRatio="none"` inside a console 350-510 px wide
 * squeezes the x axis by ~2x: every monospaced label, tick and hatch line comes out
 * compressed along one axis only — the worst possible compromise for something meant to
 * read like an instrument trace.
 */

import { useEffect, useRef, useState } from "react";

/** Fallback width when the element has not been measured yet (jsdom, first paint). */
export const FALLBACK_WIDTH = 1000;

export const useMeasuredWidth = (): [number, React.RefObject<HTMLDivElement | null>] => {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(FALLBACK_WIDTH);
  useEffect(() => {
    const node = ref.current;
    if (!node || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) => {
      const measured = Math.round(entries[0]?.contentRect.width ?? 0);
      if (measured > 0) setWidth(measured);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return [width, ref];
};
